import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {ContactGroupMap, GetListParams, Group, List} from '../../../../../../services/cellcast.v2.types'
import {
  useContactDeleteListMutation,
  useLazyContactCountListQuery,
  useLazyContactGetListQuery,
  useLazyGetContactsQuery,
} from '../../../../../../services/cellcast.v2'
import {OverlayTrigger, Spinner, Tooltip} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import moment from 'moment-timezone'
import ReactPaginate from 'react-paginate'
import NodataYet from '../../../../../Components/NodataYet'
import ListAction from './ListAction'
import WriteModal from '../Modal/WriteModal'
import ListFilter from './ListFilter'
import {useLazyGetGroupQuery} from '../../../../../../services/cellcast'
import {MenuComponent} from '../../../../../../_metronic/assets/ts/components'
import DeleteConfirmationPopup from '../../../../../Components/DeleteConfirmationPopup'
import {toast} from 'react-toastify'
import QuickMessageDrawerInner from '../../../../../../_metronic/partials/layout/drawer-messenger/Quickmessge'
import {AuthenticateUser} from '../../../../../Utils/_helpers/RoleAndPermission'
import AddToAnotherListModal from '../Modal/AddToAnotherListModal'
import MoveToAnotherListModal from '../Modal/MoveToAnotherListModal'
import {getIsDefaultRegion} from '../../../accounts/Overview/OverviewSlice/UserDetailsSlice'
import {useSelector} from 'react-redux'
import {useReadList} from '../useReadListHook'
import {useWriteList} from '../useWriteListHook'
import {isSameHour} from 'date-fns'
import { KTSVG } from '../../../../../../_metronic/helpers'
import UploadContact from '../../Components/UploadContactModal'
import AddSingleSubscriber from '../../AllContactsList/Modal/AddSingleSubscriber'
import {toAbsoluteUrl} from '../../../../../../_metronic/helpers'
import {useResponsive} from '../../../../../hooks/useResponsive'
import {ListMobileCard} from './ListMobileCard'
import './List.scss'

interface ListPageProps {
  newNavigation?: boolean
  setTotal?: (value?: number) => void
}

export const ListPage: FC<ListPageProps> = ({newNavigation, setTotal}) => {
  const navigate = useNavigate()
  const [params, setParams] = useState<GetListParams>({
    search: '',
    usePaginate: true,
    page: 1,
    limit: 10,
  })
  const [reload, setReload] = useState(true)
  const [loadedData, setLoadedData] = useState<{
    startLoad: number
    endLoad: number
  }>({
    startLoad: 0,
    endLoad: 0,
  })
  const isDefaultRegion = useSelector(getIsDefaultRegion)

  const {deleteHandler, deleteLoading} = useWriteList()

  const [showWriteModal, setShowWriteModal] = useState(false)
  const [showDeleteListModal, setShowDeleteListModal] = useState(false)
  const [showAddContactModal, setShowAddContactModal] = useState(false)
  const [selectedList, setSelectedList] = useState<List | undefined>(undefined)
  const [loadedContacts, setLoadedContacts] = useState<Map<string, ContactGroupMap[]>>(new Map())
  const [showQmDrawer, setShowQmDrawer] = useState(false)
  const [assignedListPopup, setAssignedListPopup] = useState(false)
  const [moveListPopup, setMovedListPopup] = useState(false)
  const [selectedListId, setSelectedListId] = useState('')
  const [selectedListName, setSelectedListName] = useState('')
  const [isShowPaginate, setShowPaginate] = useState(true)
  const {isMobile} = useResponsive()

  const [addSingleContactModal, setAddSingleContactModal] = useState<{
    data: any
    toggle: boolean
    group?: Group
  }>({
    data: '',
    toggle: false,
  })

  const {
    data,
    currentPage,
    totalData,
    countData,
    totalPage,
    isCountLoading,
    fetchListData,
    isLoading,
  } = useReadList()

  const getLoadedData = useCallback(
    (
      page: string | number | undefined,
      limit: string | number | undefined,
      totalData: number
    ): {
      startLoad: number
      endLoad: number
    } => {
      let loadedPage = 1
      if (typeof page === 'string') {
        loadedPage = parseInt(page) || 1
      }

      if (typeof page === 'number') {
        loadedPage = page || 1
      }

      let loadedLimit = 10
      if (typeof limit === 'string') {
        loadedLimit = parseInt(limit) || 1
      }

      if (typeof limit === 'number') {
        loadedLimit = limit || 10
      }

      const startItem = (loadedPage - 1) * loadedLimit + 1
      const endItem = Math.min(loadedPage * loadedLimit, totalData)

      return {
        startLoad: startItem,
        endLoad: endItem,
      }
    },
    []
  )

  const [fetchGroup, {data: group}] = useLazyGetGroupQuery()

  const [fetchContact, {isLoading: contactsLoading, isFetching: contactFetching}] =
    useLazyGetContactsQuery()

  const canSendQm = useCallback(
    (id: string): boolean => {
      if (countData && countData.data.length > 0) {
        const found = countData.data.find((el) => el.id === id)
        if (found) {
          return found.count <= 50
        }
      }

      return false
    },
    [countData]
  )

  const handleShowQmDrawer = useCallback(
    (list: List) => {
      if (!list._id) {
        return
      }

      if (loadedContacts.has(list._id)) {
        setShowQmDrawer(true)
        return
      }

      setSelectedList(list)
      fetchContact({
        tag: list._id,
        page: 1,
        limit: 25,
        groupId: group && group.data ? group.data._id : '',
      }).then((res) => {
        if ('data' in res && res.data) {
          loadedContacts.set(list._id, res.data ? res.data.data.items : [])
          setShowQmDrawer(true)
        }
      })
    },
    [fetchContact, group]
  )

  const handleSendBm = useCallback(
    (listId: string) => {
      navigate(`/new-message`, {
        state: {
          selectedType: 'segmentOrTags',
          data: `tag_${listId}`,
        },
      })
    },
    [navigate]
  )

  // first init
  useEffect(() => {
    fetchListData({
      page: params.page || 0,
      limit: params.limit || 10,
      search: params.search || '',
    })
    fetchGroup(null, true)
  }, [])

  useEffect(() => {
    if (data && totalPage === 0) {
      setShowPaginate(false)
    }
  }, [data])

  // first init
  useEffect(() => {
    if (reload) {
      fetchListData({
        page: params.page || 0,
        limit: params.limit || 10,
        search: params.search || '',
      })
      setReload(false)
    }
  }, [reload, params])

  useEffect(() => {
    if (data) {
      const loadedData = getLoadedData(
        currentPage ? currentPage : 1,
        params.limit,
        totalData ? totalData : 0
      )
      setLoadedData(loadedData)
    }
    MenuComponent.reinitialization()
  }, [data, isLoading])

  // Modal
  const Write = useMemo(
    () => (
      <WriteModal
        list={selectedList}
        show={showWriteModal}
        handleClose={() => {
          setShowWriteModal(false)
          setSelectedList(undefined)
        }}
        callbackFn={() => {
          setParams({...params, page: 1, limit: 10, search: ''})
          setShowWriteModal(false)
          setReload(true)
          setSelectedList(undefined)
        }}
      />
    ),
    [params, showWriteModal, selectedList]
  )

  const Delete = useMemo(
    () => (
      <DeleteConfirmationPopup
        show={showDeleteListModal}
        handleClose={() => {
          setShowDeleteListModal(false)
          setSelectedList(undefined)
        }}
        Message={` To confirm that you want to delete ${selectedList?.name} list, please type "DELETE" in the field provided below`}
        confirm={() => {
          deleteHandler(selectedList && selectedList._id ? [selectedList._id] : [], () => {
            setReload(true)
            setSelectedList(undefined)
          })
        }}
        subTitle={'These list will permanently deleted from your account.'}
        withInputConfirmation={true}
      />
    ),
    [deleteHandler, selectedList, showDeleteListModal]
  )

  const SendQuickMessage = useMemo(
    () => (
      <QuickMessageDrawerInner
        show={showQmDrawer}
        handleClose={() => setShowQmDrawer(false)}
        contacts={
          selectedList && selectedList._id && loadedContacts.has(selectedList._id)
            ? loadedContacts
                .get(selectedList._id)
                ?.map((key: any) => ({value: key?.number, label: key?.number}))
            : []
        }
      />
    ),
    [loadedContacts, showQmDrawer]
  )

  const assignedList = useMemo(
    () => (
      <AddToAnotherListModal
        callbackFn={setReload}
        selectedListId={selectedListId}
        show={assignedListPopup}
        handleClose={() => {
          setAssignedListPopup(false)
        }}
      />
    ),
    [assignedListPopup, selectedListId]
  )

  const moveList = useMemo(
    () => (
      <MoveToAnotherListModal
        callbackFn={setReload}
        selectedListId={selectedListId}
        show={moveListPopup}
        handleClose={() => {
          setMovedListPopup(false)
        }}
      />
    ),
    [moveListPopup, selectedListId]
  )

  useEffect(() => {
    if (setTotal &&  totalData !== undefined) {
      setTotal(totalData)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalData])

  const handleShowAddSingleContact = useCallback(() => {
    const mergedArray = group && group.data?.fields ? [...group.data?.fields] : []

    setAddSingleContactModal({
      data: mergedArray.map((it) => ({name: it, value: ''})),
      toggle: true,
      group: group?.data as any,
    })
  }, [group?.data])

  const AddSingleContact = useMemo(
    () => (
      <AddSingleSubscriber
        show={addSingleContactModal}
        handleClose={setAddSingleContactModal}
        callbackFn={() => {
          setSelectedListId('')
          setSelectedListName('')
        }}
        tagIds={[selectedListId]}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addSingleContactModal]
  )

  const ImportContact = useMemo(
    () => (
      <UploadContact
        show={showAddContactModal}
        listName={selectedListName || ''}
        groupId={group && group.data ? group.data._id  : ''}
        handleClose={() => {
          setShowAddContactModal(false)
        }}
        listId={selectedListId}
        showAddSingleContact={handleShowAddSingleContact}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showAddContactModal, selectedListId, group?.data?._id, selectedListId]
  )

  const handleAddContact = (list: any) => {
    setShowAddContactModal(true)
    setSelectedListId(list?._id)
    setSelectedListName(list?.name)
  }

  return (
    <>
      {Write}
      {Delete}
      {SendQuickMessage}
      {assignedList}
      {moveList}
      {ImportContact}
      {AddSingleContact}
      <div className='card mb-0'>
        <div className={`card-header border-0 ${isMobile ? 'px-4' : ''} ${isMobile ? 'list-page-header-mobile' : ''}`}>
          {!newNavigation ? (
            <div className='d-flex flex-column w-100'>
              <h3 className='card-title align-items-start  flex-column'>
                <span className='card-label fw-bolder fs-2 mb-1'>List</span>
                <span className='text-muted mt-1 fw-bold fs-7'>Here you can see all your list</span>
              </h3>
            </div>
          ) : null}
          <div className={isMobile ? 'd-flex flex-column gap-3 mt-3 px-4' : 'w-100 d-flex justify-content-between align-items-center'}>
            <ListFilter params={params} setParams={setParams} doFetch={() => setReload(true)} />
            <ListAction
              showModalCreate={() => setShowWriteModal(true)}
              showModalCreateContact={() => setShowAddContactModal(true)}
            />
          </div>
        </div>
        {isLoading ? (
          <div className='row flex-center h-200 mb-10'>
            <Spinner />
          </div>
        ) : (
          <>
            {data.length > 0 ? (
              <div className={`card-body ${isMobile ? 'px-4' : ''}`}>
                {isMobile ? (
                  <div className='mobile-lists-list'>
                    {data?.map((item, i: number) => {
                      const itemContactCount = isDefaultRegion
                        ? isCountLoading
                          ? 'counting....'
                          : countData && countData.data.length > 0
                          ? countData.data.find((el) => el.id === item._id)?.count
                          : 0
                        : item.contactCount
                      return (
                        <ListMobileCard
                          key={i}
                          list={item}
                          isDefaultRegion={isDefaultRegion}
                          contactCount={itemContactCount}
                          isCountLoading={isCountLoading}
                          canSendQm={canSendQm(item._id)}
                          onEdit={(list) => {
                            setSelectedList(list)
                            setShowWriteModal(true)
                          }}
                          onDelete={(list) => {
                            setSelectedList(list)
                            setShowDeleteListModal(true)
                          }}
                          onAddContact={handleAddContact}
                          onSendBulkMessage={handleSendBm}
                          onSendQuickMessage={handleShowQmDrawer}
                          onCopyToList={(listId) => {
                            setSelectedListId(listId)
                            setAssignedListPopup(true)
                          }}
                          onMoveToList={(listId) => {
                            setSelectedListId(listId)
                            setMovedListPopup(true)
                          }}
                          onManageContacts={(list) => {
                            navigate('/contacts/list/' + list._id, {
                              state: {
                                title: list.name,
                              },
                            })
                          }}
                        />
                      )
                    })}
                  </div>
                ) : (
                  /* Desktop view: Table layout */
                  <div className='table-responsive'>
                    <table className='table align-middle gs-0 gy-5 all-message-table'>
                      <thead>
                        <tr className='fw-400 text-muted'>
                          <th>LIST NAME</th>
                          <th>CREATED</th>
                          <th>UPDATED</th>
                          <th>CONTACTS</th>
                          <th className='w-100px'>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td colSpan={5} className='text-center mb-10'>
                              <div className='text-center'>
                                <img src={toAbsoluteUrl('/media/misc/spinner200.gif')} alt='loading' width={80} />
                              </div>
                            </td>
                          </tr>
                        ) : (
                          <>
                            {data?.map((item, i: number) => (
                              <tr key={i}>
                                <td
                                  onClick={() => navigate('/contacts/list/' + item._id)}
                                  className='fw-bold cursor-pointer px-2'
                                >
                                  {item?.name}
                                </td>
                                <td>{moment(item?.createdAt).format('DD/MM/YYYY LT')}</td>
                                <td>{moment(item?.updatedAt).format('DD/MM/YYYY LT')}</td>
                                {isDefaultRegion ? (
                                  <td>
                                    {isCountLoading
                                      ? 'counting....'
                                      : countData && countData.data.length > 0
                                      ? countData.data.find((el) => el.id === item._id)?.count
                                      : 0}
                                  </td>
                                ) : (
                                  <td>{item.contactCount}</td>
                                )}

                                <td>
                                  <div className='d-flex gap-1'>
                                    {AuthenticateUser('upload-contacts') && (
                                      <OverlayTrigger
                                        placement='top'
                                        overlay={<Tooltip id='button-tooltip'>Add contacts</Tooltip>}
                                      >
                                        <button
                                          type='button'
                                          className='btn btn-sm btn-icon btn-light-primary'
                                          onClick={() => handleAddContact(item)}
                                        >
                                          <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
                                        </button>
                                      </OverlayTrigger>
                                    )}

                                    {(AuthenticateUser('send-quick-sms') ||
                                      AuthenticateUser('edit-contact')) && (
                                      <span
                                        className='btn btn-sm btn-icon btn-bg-light btn-active-color-primary'
                                        data-kt-menu-trigger='hover'
                                        data-kt-menu-placement='bottom-end'
                                      >
                                        {(deleteLoading || contactsLoading || contactFetching) &&
                                        selectedList &&
                                        selectedList._id === item._id ? (
                                          <Spinner animation='grow' size='sm' />
                                        ) : (
                                          <i className='bi bi-three-dots fs-5'></i>
                                        )}
                                      </span>
                                    )}
                                    <div
                                      className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-state-bg-light-primary fw-bold fs-7 w-200px py-4'
                                      data-kt-menu={
                                        deleteLoading || contactsLoading || contactFetching
                                          ? 'false'
                                          : 'true'
                                      }
                                    >
                                      {AuthenticateUser('upload-contacts') && (
                                        <div className='menu-item px-3'>
                                          <span
                                            className='menu-link px-3'
                                            onClick={() => handleAddContact(item)}
                                          >
                                            <i className='bi bi-person-lines-fill me-2'></i>Add Contacts
                                          </span>
                                        </div>
                                      )}
                                      {AuthenticateUser('send-quick-sms') && (
                                        <div className='menu-item px-3'>
                                          <span
                                            className='menu-link px-3'
                                            onClick={() => handleSendBm(item._id)}
                                          >
                                            <i className='bi bi-send-fill me-2'></i>Send Bulk Message
                                          </span>
                                        </div>
                                      )}
                                      {AuthenticateUser('send-quick-sms') && canSendQm(item._id) && (
                                        <div className='menu-item px-3'>
                                          <span
                                            className='menu-link px-3'
                                            onClick={() => handleShowQmDrawer(item)}
                                          >
                                            <i className='bi bi-send me-2'></i>Send Quick Message
                                          </span>
                                        </div>
                                      )}

                                      {AuthenticateUser('edit-contact') && (
                                        <div className='menu-item px-3'>
                                          <span
                                            className='menu-link px-3'
                                            onClick={() => {
                                              setSelectedList(item)
                                              setShowWriteModal(true)
                                            }}
                                          >
                                            <i className='bi bi-pencil me-2'></i>Edit List Name
                                          </span>
                                        </div>
                                      )}
                                      {AuthenticateUser('edit-contact') && isDefaultRegion && (
                                        <div className='menu-item px-3'>
                                          <span
                                            className='menu-link px-3'
                                            onClick={() => {
                                              setSelectedListId(item?._id)
                                              setAssignedListPopup(true)
                                            }}
                                          >
                                            <i className='bi bi-list me-2'></i>Copy to another List
                                          </span>
                                        </div>
                                      )}

                                      {AuthenticateUser('edit-contact') && isDefaultRegion && (
                                        <div className='menu-item px-3'>
                                          <span
                                            className='menu-link px-3'
                                            onClick={() => {
                                              setSelectedListId(item?._id)
                                              setMovedListPopup(true)
                                            }}
                                          >
                                            <i className='bi bi-scissors me-2'></i>Move to another
                                            List
                                          </span>
                                        </div>
                                      )}

                                      {AuthenticateUser('edit-contact') && (
                                        <div className='menu-item px-3'>
                                          <span
                                            className='menu-link px-3'
                                            onClick={() =>
                                              navigate('/contacts/list/' + item._id, {
                                                state: {
                                                  title: item.name,
                                                },
                                              })
                                            }
                                          >
                                            <i className='bi bi-person-lines-fill me-2'></i>Manage
                                            Contact
                                          </span>
                                        </div>
                                      )}

                                      <div className='menu-item px-3'>
                                        <span
                                          className='menu-link px-3'
                                          onClick={() => {
                                            navigator.clipboard.writeText(item._id)
                                            toast.success('Successfully copied list id')
                                          }}
                                        >
                                          <i className='bi bi-clipboard me-2'></i>Copy ID
                                        </span>
                                      </div>

                                      {AuthenticateUser('edit-contact') && (
                                        <div className='menu-item px-3'>
                                          <span
                                            className='menu-link px-3 text-danger'
                                            onClick={() => {
                                              setSelectedList(item)
                                              setShowDeleteListModal(true)
                                            }}
                                          >
                                            <i className='bi bi-trash me-2 text-danger'></i>Delete
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              <>
                {isLoading ? (
                  <div className='row flex-center h-200'>
                    <Spinner />
                  </div>
                ) : (
                  <NodataYet message={`You don't have any list yet`} />
                )}
              </>
            )}
          </>
        )}
        {isShowPaginate && (
          <div className={`abcd ps-10 pe-5 pb-5 ${isMobile ? 'px-4' : ''}`}>
            <select
              onChange={(e: any) => {
                setParams({...params, limit: e.target.value})
                setReload(true)
              }}
              value={params.limit || 10}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            {isDefaultRegion && (
              <>
                {isLoading && isDefaultRegion
                  ? '. . . . .'
                  : `Show ${loadedData.startLoad} - ${loadedData.endLoad} of ${
                      totalData ? totalData : 0
                    } data`}
              </>
            )}
            <ReactPaginate
              previousLabel='<'
              nextLabel='>'
              pageCount={totalPage || 0}
              onPageChange={(e: any) => {
                setParams({...params, page: e.selected + 1})
                setReload(true)
              }}
              containerClassName={'pagination'}
              previousLinkClassName={'pagination__link'}
              nextLinkClassName={'pagination__link'}
              disabledClassName={'pagination__link--disabled'}
              activeClassName={'pagination__link--active'}
            />
          </div>
        )}
      </div>
    </>
  )
}

export default ListPage
